/* eslint-disable react/jsx-key */
import React from 'react';
import PT from 'prop-types';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';
import SVG from 'react-inlinesvg';
import _compact from 'lodash/compact';
import personPlusIcon from '@lifesize/ux-assets/vectors/svgIcons/person-plus.svg';
import cancelIcon from '@lifesize/ux-assets/vectors/svgIcons/cancel.svg';
import handIcon from '@lifesize/ux-assets/vectors/svgIcons/hand.svg';
import personLockIcon from '@lifesize/ux-assets/vectors/svgIcons/person-lock.svg';
import micIcon from '@lifesize/ux-assets/vectors/svgIcons/mic.svg';
import micOffIcon from '@lifesize/ux-assets/vectors/svgIcons/mic-off.svg';
import searchIcon from '@lifesize/ux-assets/vectors/svgIcons/search.svg';
import Tooltip from 'components/Tooltip/Tooltip';
import LockedMeetingTooltip from 'components/Tooltip/LockedMeetingTooltip';
import { participantFilterType } from 'constants/ParticipantList';
import InCallParticipant from './InCallParticipant.js';
import classes from './InCallParticipantList.module.scss';

const messages = defineMessages({
  search: { id: 'components.inCall.participantList.search', defaultMessage: 'Search' }
});

export class InCallParticipantList extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      searchTerm: ''
    };
  }

  getFilteredParticipants = (list) => {
    // filter the participants that include any part of the searchTerm
    return list.filter(participant => {
      const searchTerms = this.state.searchTerm.trim().toLowerCase().split(' ');
      const nameString = (participant.name || participant.displayName || '').toLowerCase();
      return !!searchTerms.find(term => nameString.includes(term));
    })
  }

  handleFilterNone = () => {
    this.props.selectParticipantFilter(participantFilterType.none);
  };

  handleFilterHandsRaised = () => {
    this.props.selectParticipantFilter(participantFilterType.raisedHands);
  };

  handleFilterUnmuted = () => {
    this.props.selectParticipantFilter(participantFilterType.unmuted);
  };

  handleSearchChange = (searchTerm = '') => {
    this.setState({ searchTerm }, () => {
      if (!searchTerm) {
        this.searchInput.focus();
      }
    });
  }

  renderEmptyListMessage = (reason) => {
    switch (reason) {
      case 'searching':
        return (
          <FormattedMessage
            id="components.inCall.participantList.noSearchResults"
            defaultMessage={'No participants matched your search'}
          />
        );
      case participantFilterType.raisedHands:
        return (
          <FormattedMessage
            id="components.inCall.participantList.noRaisedHands"
            defaultMessage={'There are no hands raised'}
          />
        );
      case participantFilterType.unmuted:
        return (
          <FormattedMessage
            id="components.inCall.participantList.noUnmuted"
            defaultMessage={'There are no unmuted participants'}
          />
        );
      default:
        return (
          <FormattedMessage
            id="components.inCall.participantList.loadingParticipants"
            defaultMessage={'Retrieving the participant list...'}
          />
        );
    }
  };

  render() {
    const participantClassNames = _compact([
      classes.participantListContainer,
      this.props.isVisible ? '' : classes.hiddenDrawer
    ]).join(' ');

    const {
      callFullyConnected,
      canChangeMeetingLock,
      canMuteAll,
      isGuest,
      isLockedByModerator,
      isMeetingLocked,
      isAudioMutedByServer,
      isModerator,
      lecturers,
      moderators,
      onAddParticipantClicked,
      onLowerAllHandsClicked,
      onLowerHandClicked,
      onLockMeetingClicked,
      onMuteAllClicked,
      onMuteParticipantClicked,
      onSelectRaisedHandClicked,
      onStopPresenterClicked,
      participantFilter,
      participants,
      participantsCount,
      raisedHandsCount,
      remoteModerators,
      removeFromMeetingClicked,
      unmutedCount,
      showLowerAllHandsButton
    } = this.props;

    const filteredParticipants = !!this.state.searchTerm
      ? this.getFilteredParticipants(participants)
      : participants;

    const filteredModerators = !!this.state.searchTerm
        ? this.getFilteredParticipants(moderators)
        : moderators;

    const filteredRemoteModerators = !!this.state.searchTerm
      ? this.getFilteredParticipants(remoteModerators)
      : remoteModerators;

    const filteredLecturers = !!this.state.searchTerm
      ? this.getFilteredParticipants(lecturers)
      : lecturers;

    if (!callFullyConnected) {
      return (
        <div className={participantClassNames}>
          <header className={classes.loadingHeader}>
            <h6>
              <FormattedMessage
                id="components.inCall.participantList.loadingParticipants"
                defaultMessage={'Retrieving the participant list...'}
              />
            </h6>
          </header>
        </div>
      );
    }

    return (
      <div className={participantClassNames}>
        <header>
          <h6 data-testid="participantCount">
            <FormattedMessage
              id="components.inCall.participantList.header"
              description="In call participant list header"
              defaultMessage="Participants ({participants, number})"
              values={{ participants: (participantsCount) }}
            />
          </h6>
          {this.props.allowParticipantSearch &&
            <section className={classes.searchContainer}>
              <div className={classes.inputGroup}>
                <input
                  autoComplete="off"
                  data-testid="participant-search-input"
                  maxLength={64}
                  onChange={e => this.handleSearchChange(e.target.value)}
                  placeholder={this.props.intl.formatMessage(messages.search)}
                  ref={(elt) => { this.searchInput = elt; }}
                  type="text"
                  value={this.state.searchTerm}
                />
                <SVG src={searchIcon} />
                {!!this.state.searchTerm &&
                  <button onClick={() => this.handleSearchChange()}>
                    <SVG src={cancelIcon} data-testid="cancelIcon" />
                  </button>
                }
              </div>
            </section>
          }
          {
            showLowerAllHandsButton &&
            <section className={classes.filterContainer}>
              <button data-testid="allParticipants" disabled={participantFilter === participantFilterType.none} onClick={this.handleFilterNone}>
                <FormattedMessage
                  id="components.inCall.participantList.filter.all"
                  defaultMessage="All"
                />
              </button>
              <button data-testid="handRaisedParticipants" disabled={participantFilter === participantFilterType.raisedHands} onClick={this.handleFilterHandsRaised}>
                <SVG src={handIcon} className={!!raisedHandsCount ? classes.raisedHands : ''} />
                <FormattedMessage
                  id="components.inCall.participantList.filter.raisedHands"
                  defaultMessage="{handsRaised, number} raised {handsRaised, plural, one {hand} other {hands}}"
                  values={{ handsRaised: raisedHandsCount }}
                />
              </button>
              <button data-testid="unmutedParticipants" disabled={participantFilter === participantFilterType.unmuted} onClick={this.handleFilterUnmuted}>
                <SVG src={micIcon} data-testid="micIcon" />
                <FormattedMessage
                  id="components.inCall.participantList.filter.unmuted"
                  defaultMessage="{unmutedParticipants, number} unmuted"
                  values={{ unmutedParticipants: unmutedCount }}
                />
              </button>
            </section>
          }
        </header>
        <div className={classes.participantList}>
          {!filteredParticipants.length && (
            <p className={classes.emptyListMessage} data-testid="emptyParticipantListMessage">
              {this.renderEmptyListMessage(this.state.searchTerm ? 'searching' : this.props.participantFilter)}
            </p>
          )}
          {filteredParticipants.map((participant, index) =>
            <InCallParticipant
              canBeMuted={canMuteAll}
              key={`participant-${participant.participantId}-${index}`}
              onLowerHandClicked={onLowerHandClicked}
              onMuteParticipantClicked={onMuteParticipantClicked}
              onRemoveFromMeetingClicked={removeFromMeetingClicked}
              onSelectRaisedHandClicked={onSelectRaisedHandClicked}
              onStopPresenterClicked={onStopPresenterClicked}
              userIsModerator={isModerator}
              participant={participant}
            />
          )}
          {(participantFilter === participantFilterType.none) &&
            <div className={classes.participantRoles}>
              {!!moderators.length &&
                <div>
                  <FormattedMessage
                    id="components.inCall.participantList.moderatorLabel"
                    description="List of meeting moderators"
                    defaultMessage={'Moderators:'}
                  />
                  {!!filteredModerators.length
                    ? (filteredModerators.map((m, index) => <p data-testid="moderator" key={`moderator-${index}`}>{m.name}</p>))
                    : (
                      <p data-testid="moderator">
                        <FormattedMessage
                          id="components.inCall.participantList.noSearchResultsModerators"
                          defaultMessage={'No moderators matched your search'}
                        />
                      </p>
                    )
                  }
                </div>
              }
              {!!remoteModerators.length &&
                <div>
                  <FormattedMessage
                    id="components.inCall.participantList.remoteModeratorLabel"
                    defaultMessage={'Remote Moderators:'}
                  />
                  {filteredRemoteModerators.length
                    ? (filteredRemoteModerators.map((m, index) => <p data-testid="remoteModerator" key={`remoteModerator-${index}`}>{m.displayName}</p>))
                    : (
                      <p data-testid="remoteModerator">
                        <FormattedMessage
                          id="components.inCall.participantList.noSearchResultsRemoteModerators"
                          defaultMessage={'No remote moderators matched your search'}
                        />
                      </p>
                    )
                  }
                </div>
              }
              {!!lecturers.length &&
                <div>
                  <FormattedMessage
                    id="components.inCall.participantList.lecturerLabel"
                    description="In call participant list lecturer label"
                    defaultMessage={'Lecturer:'}
                  />
                  {filteredLecturers.length
                    ? (filteredLecturers.map((m, index) => <p data-testid="lecturer" key={`lecturer-${index}`}>{m.name}</p>))
                    : (
                      <p data-testid="lecturer">
                        <FormattedMessage
                          id="components.inCall.participantList.noSearchResultsLecturers"
                          defaultMessage={'No lecturers matched your search'}
                        />
                      </p>
                    )
                  }
                </div>
              }
            </div>
          }
        </div>
        <div className={classes.buttonContainer}>
          {(canMuteAll && !isAudioMutedByServer) &&
            <button
              data-testid="btnMuteAll"
              key="muteAll"
              onClick={() => {
                onMuteAllClicked(true);
              }}
            >
              <div className={classes.tooltipWrapper}>
                <Tooltip
                  location={'top'}
                  overlapCarat={false}
                  label={
                    <FormattedMessage
                      id="contacts.tooltip.muteAll" defaultMessage="Participants muted by the moderator cannot unmute themselves."
                    />
                  }
                />
              </div>
              <SVG src={micOffIcon} data-testid="micOffIcon" className={classes.buttonIcon} />
              <FormattedMessage
                id="components.inCall.mute"
                defaultMessage="Mute All"
              />
            </button>
          }
          {(canMuteAll && isAudioMutedByServer) &&
            <button
              data-testid="btnUnmuteAll"
              key="unmuteAll"
              onClick={() => {
                onMuteAllClicked(false);
              }}
            >
              <div className={classes.tooltipWrapper}>
                <Tooltip
                  location={'top'}
                  overlapCarat={false}
                  label={
                    <FormattedMessage
                      id="contacts.tooltip.unmuteAll"
                      defaultMessage="Unmute participants. Self-muted participants will remain muted."
                    />
                  }
                />
              </div>
              <SVG src={micIcon} className={classes.buttonIcon} />
              <FormattedMessage
                id="components.inCall.unMute"
                defaultMessage="Unmute All"
              />
            </button>
          }
          {!isGuest &&
            <button
              data-testid="btnAddParticipant"
              disabled={isLockedByModerator}
              onClick={onAddParticipantClicked}
            >
              {isLockedByModerator && <LockedMeetingTooltip />}
              <SVG src={personPlusIcon} data-testid="personPlusIcon" className={classes.buttonIcon} />
              <FormattedMessage
                id="components.inCall.addPart"
                defaultMessage="Add a Participant"
              />
            </button>
          }
          {isModerator && showLowerAllHandsButton &&
            <button
              data-testid="btnLowerAllHands"
              disabled={!raisedHandsCount}
              onClick={onLowerAllHandsClicked}
            >
              <SVG src={handIcon} className={classes.buttonIcon} />
              <FormattedMessage
                id="components.inCall.lowerAllHands"
                defaultMessage="Lower All Hands"
              />
            </button>
          }
          {isModerator &&
            <button
              data-testid={isMeetingLocked ? 'btnUnlockMeeting' : 'btnLockMeeting'}
              disabled={!canChangeMeetingLock}
              onClick={() => onLockMeetingClicked(isMeetingLocked)}
            >
              <SVG src={personLockIcon} data-testid="personLockIcon" className={classes.buttonIcon} />
              {isMeetingLocked ? (
                <FormattedMessage
                  id="components.inCall.unlockMeeting"
                  defaultMessage="Unlock this Meeting"
                />
              ) : (
                <FormattedMessage
                  id="components.inCall.lockMeeting"
                  defaultMessage="Lock this Meeting"
                />
              )}
            </button>
          }
        </div>
      </div >
    );
  }
}

InCallParticipantList.propTypes = {
  allowParticipantSearch: PT.bool,
  callFullyConnected: PT.bool,
  canChangeMeetingLock: PT.bool,
  canMuteAll: PT.bool,
  intl: PT.object,
  isGuest: PT.bool,
  isLockedByModerator: PT.bool,
  isMeetingLocked: PT.bool,
  isAudioMutedByServer: PT.bool,
  isModerator: PT.bool,
  isVisible: PT.bool,
  lecturers: PT.array,
  moderators: PT.array,
  onAddParticipantClicked: PT.func.isRequired,
  onLowerAllHandsClicked: PT.func.isRequired,
  onLockMeetingClicked: PT.func.isRequired,
  onLowerHandClicked: PT.func.isRequired,
  onMuteAllClicked: PT.func.isRequired,
  onMuteParticipantClicked: PT.func.isRequired,
  onSelectRaisedHandClicked: PT.func.isRequired,
  onStopPresenterClicked: PT.func.isRequired,
  participantFilter: PT.string, // ownProps
  participants: PT.array,
  participantsCount: PT.number,
  raisedHandsCount: PT.number,
  remoteModerators: PT.array.isRequired,
  removeFromMeetingClicked: PT.func,
  selectParticipantFilter: PT.func.isRequired, // ownProps
  unmutedCount: PT.number,
  showLowerAllHandsButton: PT.bool
};

export default injectIntl(InCallParticipantList);
